import { Field, Fieldset, Label } from "@headlessui/react";
import { Checkbox } from "antd";
import { useState } from "react";
import { Link } from "react-router";
import logo from "../assets/logo.png";
import { cleanPhone } from "../functions/cleanPhone";
import { Gradient } from "./Gradient";
import { Button } from "./tailwind/button";
import { Input } from "./tailwind/input";
import { Text } from "./tailwind/text";

export const SignUpPage = ({ setShowSignUp, handleSignUp, isLoading }) => {
  const [companyName, setCompanyName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });

  const validate = () => {
    const newErrors = {
      email: "",
      password: "",
      companyName: "",
      firstName: "",
      lastName: "",
    };
    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email address is invalid";
    }
    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }
    if (!companyName) {
      newErrors.companyName = "Company Name is required";
    }
    if (!firstName) {
      newErrors.firstName = "First Name is required";
    }
    if (!lastName) {
      newErrors.lastName = "Last Name is required";
    }

    setErrors(newErrors);
    return (
      !newErrors.email &&
      !newErrors.password &&
      !newErrors.companyName &&
      !newErrors.firstName &&
      !newErrors.lastName
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      handleSignUp({
        companyName: companyName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase().trim(),
        password,
        phone,
      });
    }
  };

  const [checked, setChecked] = useState(false);

  const onChange = (e) => {
    setChecked(e.target.checked);
  };

  return (
    <Gradient>
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img className="mx-auto h-10 w-auto" src={logo} alt="Simio" />
          <h2 className="mt-6 text-center text-2xl font-bold font-title leading-9 tracking-tight text-gray-900">
            Create your account
          </h2>
        </div>

        <Text className="mt-5 text-center">
          Want to learn more about Simio?{" "}
          <Link
            onClick={() =>
              window.open(
                "https://calendly.com/tiphaine-simio/30mn?utm_source=sign_up_page",
                "_blank",
              )
            }
            className="font-semibold text-purple hover:text-purple/80"
          >
            Book a demo
          </Link>
        </Text>

        <div className="mt-5 sm:mx-auto sm:w-full sm:max-w-[480px] z-20">
          <div className="bg-white px-6 py-5 shadow sm:rounded-lg sm:px-12">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <Fieldset>
                <Field>
                  <Label>Company Name</Label>
                  <Input
                    name="companyName"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    {...(errors.companyName && { "data-invalid": true })}
                  />
                  {errors.companyName && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.companyName}
                    </p>
                  )}
                </Field>
                <br />
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <Label>First Name</Label>
                    <Input
                      name="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      {...(errors.firstName && { "data-invalid": true })}
                    />
                    {errors.firstName && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.firstName}
                      </p>
                    )}
                  </Field>
                  <Field>
                    <Label>Last Name</Label>
                    <Input
                      name="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      {...(errors.lastName && { "data-invalid": true })}
                    />
                    {errors.lastName && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.lastName}
                      </p>
                    )}
                  </Field>
                </div>
                <br />
                <Field>
                  <Label>Email Address</Label>
                  <Input
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    {...(errors.email && { "data-invalid": true })}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </Field>
                <br />
                <Field>
                  <Label>Password</Label>
                  <Input
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    {...(errors.password && { "data-invalid": true })}
                  />
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.password}
                    </p>
                  )}
                </Field>
                <br />
                <Field>
                  <Label>Phone Number - to receive test messages</Label>
                  <Input
                    value={phone}
                    maxLength={15}
                    onChange={(e) =>
                      setPhone(
                        cleanPhone(e.target.value.replace(/[^0-9]/g, "")),
                      )
                    }
                    autoComplete="off"
                  />
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "#999",
                      marginTop: "0.25rem",
                    }}
                  >
                    Please include your country code (e.g., 33601020304)
                  </p>
                </Field>
                <br />

                <Checkbox checked={checked} onChange={onChange}>
                  I accept Simio's{" "}
                  <Link
                    to="https://help.getsimio.com/fr_FR/conditions-generales"
                    target="_blank"
                  >
                    Terms and Conditions
                  </Link>
                </Checkbox>
              </Fieldset>

              <Button
                type="submit"
                color="primaryButton"
                className="flex w-full justify-center bg-yellow font-title font-bold leading-6"
                loading={isLoading}
                disabled={!checked}
              >
                Sign up
              </Button>
            </form>
          </div>

          <Text className="mt-10 text-center">
            Already a member?{" "}
            <Link
              onClick={() => setShowSignUp(false)}
              className="font-semibold text-purple hover:text-purple/80"
            >
              Sign in instead
            </Link>
          </Text>
        </div>
      </div>
    </Gradient>
  );
};
